import { global } from '../../core/GlobalState';
import { DEFAULT_SCENE_POLICY, getScenePolicy } from '../../core/ScenePolicies';
/** Update the global policy cache for the given scene name. */
function updateGlobalPolicyCacheForScene(sceneName) {
    var _a;
    global.cachedSceneName = sceneName;
    const p = (_a = getScenePolicy(sceneName)) !== null && _a !== void 0 ? _a : DEFAULT_SCENE_POLICY;
    // radius
    if (typeof p.radius === 'number' && Number.isFinite(p.radius) && p.radius > 0) {
        global.cachedHasRadiusCap = true;
        global.cachedRadiusCap = p.radius;
    }
    else {
        global.cachedHasRadiusCap = false;
        global.cachedRadiusCap = 0;
    }
    // floor (preserve legacy default y=2 if not specified)
    if (typeof p.floorY === 'number' && Number.isFinite(p.floorY)) {
        global.cachedHasFloorCap = true;
        global.cachedFloorY = p.floorY;
    }
    else {
        global.cachedHasFloorCap = true;
        global.cachedFloorY = 2;
    }
}
function updateHistoryForScene(sceneName, options = {}) {
    var _a;
    if (typeof window === 'undefined')
        return;
    if (!window.history || !window.history.pushState)
        return;
    if ((_a = global.routing) === null || _a === void 0 ? void 0 : _a.suppressHistoryUpdate)
        return;
    const { replace = false } = options;
    const method = replace ? 'replaceState' : 'pushState';
    const definition = global.worldRegistry
        .listWorlds()
        .find(world => world.sceneName === sceneName);
    const desiredPath = definition ? `/${definition.slug}` : '/';
    if (window.location.pathname === desiredPath)
        return;
    try {
        const state = definition ? { world: definition.slug } : {};
        window.history[method](state, '', desiredPath);
    }
    catch (error) {
        console.warn('[Routing] Failed to update history path', error);
    }
}
/**
 * Enter the given portal orb:
 * - switch to its target scene
 * - spawn near the linked return orb if present
 */
export function enterPortal(orb) {
    var _a, _b;
    const config = orb.orbConfig;
    const target = config === null || config === void 0 ? void 0 : config.portalTargetSceneName;
    if (!target)
        return;
    let destinationScene = target;
    const registry = global.worldRegistry;
    if (config === null || config === void 0 ? void 0 : config.portalTargetWorldSlug) {
        registry.ensureScene(config.portalTargetWorldSlug);
        const world = registry.getWorld(config.portalTargetWorldSlug);
        if (world) {
            destinationScene = world.sceneName;
        }
    }
    else {
        const world = registry.getWorldBySceneName(target);
        if (world) {
            registry.ensureScene(world.slug);
            destinationScene = world.sceneName;
        }
    }
    switchScene(destinationScene);
    // spawn the player near the return portal in the target scene
    const om = global.orbManager;
    const linked = orb.linkedPortalUuid ? om.getOrb(orb.linkedPortalUuid) : undefined;
    const controls = global.controls;
    const fallbackY = 5;
    if (linked && linked.group) {
        const spawn = linked.group.position.clone();
        const r = (_b = (_a = orb.orbConfig) === null || _a === void 0 ? void 0 : _a.radius) !== null && _b !== void 0 ? _b : 100;
        spawn.z += r * 2;
        controls.getObject().position.copy(spawn);
    }
    else {
        controls.getObject().position.set(0, fallbackY, 0);
    }
}
/**
 * Switch to a scene by name and:
 *  - rebind postprocessing passes
 *  - move the controls' object between scenes
 *  - update the global cached movement policy
 *  - (optionally) unload the scene from two hops ago
 */
export function switchScene(nextName, options) {
    var _a;
    const unloadTwoHops = (_a = options === null || options === void 0 ? void 0 : options.unloadTwoHops) !== null && _a !== void 0 ? _a : false;
    const sm = global.sceneManager;
    const prevScene = global.scene;
    // Track scene history for unload policy
    global.previousPreviousSceneName = global.previousSceneName;
    global.previousSceneName = sm.getCurrentSceneName();
    // Set current + get the scene
    sm.setCurrentScene(nextName);
    const nextScene = sm.getScene(nextName);
    global.scene = nextScene;
    // Move the controls' object into the active scene (harmless if already removed/added)
    if (prevScene) {
        try {
            prevScene.remove(global.controls.getObject());
        }
        catch (_b) { }
    }
    try {
        nextScene.add(global.controls.getObject());
    }
    catch (_c) { }
    // Rebind passes so composer renders the new scene
    if (global.renderPass) {
        global.renderPass.scene = nextScene;
    }
    if (global.outlinePass) {
        global.outlinePass.renderScene = nextScene; // OutlinePass stores it in renderScene
        global.outlinePass.selectedObjects = []; // avoid stale refs across scenes
    }
    // ---- Update the global policy cache (zero-cost per-frame checks) ----
    updateGlobalPolicyCacheForScene(nextName);
    // ---- Unload "two hops ago" scene to keep memory in check ----
    const twoHops = global.previousPreviousSceneName;
    if (unloadTwoHops && twoHops && twoHops !== nextName && twoHops !== sm.getCurrentSceneName()) {
        try {
            if (sm.hasScene(twoHops)) {
                sm.deleteScene(twoHops, { dispose: true });
                console.log('[SceneManager] Unloaded inactive scene', { sceneName: twoHops });
            }
        }
        catch (e) {
            console.warn(`[SceneManager] Failed to unload ${twoHops}`, e);
        }
    }
    updateHistoryForScene(nextName);
    console.log(`[SceneManager] Switched to: ${nextName}`);
}
